import axios from 'axios'
import React, { useEffect } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { base64url } from '../../../Api/base64url'
import { devURL } from '../../../utils'

const RELYING_PARTY_NAME = 'Dfns'
const RELYING_PARTY_ID = window.location.hostname

const base64ToBase64Url = (b64) => {
  return b64.replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_')
}

const arrayBufferToBase64 = (buffer) => {
  let binary = ''
  const bytes = new Uint8Array(buffer)
  const len = bytes.byteLength
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return window.btoa(binary)
}

const arrayBufferToBase64Url = (buffer) => {
  return base64ToBase64Url(arrayBufferToBase64(buffer))
}

const createRandomChallenge = () => {
  const str = Math.random().toString(36).slice(2)
  return base64ToBase64Url(window.btoa(str))
}

/**
 * This function is the important piece.
 * It simply creates a new WebauthN credential locally (will prompt the user), and builds the payload to be sent to your server
 */
const createWebauthnCredential = async (args) => {
  const { userId, userName, userDisplayName } = args

  const randomChallenge = createRandomChallenge()

  const credential = await navigator.credentials.create({
    publicKey: {
      challenge: new TextEncoder().encode(randomChallenge),
      rp: {
        id: RELYING_PARTY_ID,
        name: RELYING_PARTY_NAME,
      },
      user: {
        id: new TextEncoder().encode(userId),
        name: userName,
        displayName: userDisplayName,
      },
      attestation: 'direct',
      excludeCredentials: [],
      authenticatorSelection: {
        userVerification: 'required',
        requireResidentKey: true,
        residentKey: 'required',
      },
      pubKeyCredParams: [
        { type: 'public-key', alg: -257 },
        { type: 'public-key', alg: -7 },
      ],
      timeout: 60000,
    },
  })

  if (!credential) {
    throw Error('Could not create credential')
  }

  const attestationResponse = credential.response
  const clientData = {
    type: 'key.create',
    challenge: base64url(randomChallenge),
    origin: window.location.origin,
    crossOrigin: false,
  }

  const createDfnsCredentialPayload = {
    username: userName,
    firstFactor: {
      challengeIdentifier: 'HARD-RECOVERY',
      credentialName: 'Default Credential (Post-recovery)',
      credentialKind: 'Fido2',
      credentialInfo: {
        credId: credential.id,
        clientData: arrayBufferToBase64Url(attestationResponse.clientDataJSON),
        attestationData: arrayBufferToBase64Url(
          attestationResponse.attestationObject
        ),
      },
    },
    clientData,
  }
  try {
    const res = await axios.post(
      `${devURL}/dfns/hard-recovery`,
      createDfnsCredentialPayload,
      {
        headers: { token: localStorage.getItem('swToken') },
      }
    )
    console.log(res)
    toast.success('Recovery started successfully, we will notify you soon')
    return true
  } catch (err) {
    console.log(err)
    toast.error('Something went wrong, try again later')
  }
  // send this new credential info to your server
  return false
}
function HardRecovery() {
  const navigate = useNavigate()
  const [user, setUser] = React.useState({})
  const [loading, setLoading] = React.useState(true)
  const [searchParams] = useSearchParams()

  useEffect(() => {
    getUserProfileData()
  }, [])

  if (localStorage.getItem('swToken') === null) {
    return <Navigate to='/Login' />
  }
  const getUserProfileData = async () => {
    setLoading(true)
    try {
      const res = await axios.get(`${devURL}/user/get-user-info`, {
        headers: { token: localStorage.getItem('swToken') },
      })
      if (!res.data.dfns_user_id) {
        toast.warning('Please register your wallet first')
        return navigate('/Dashboard')
      }
      if (
        res.data.dfns_recovery_credential_id &&
        searchParams.get('hard') !== 'true'
      ) {
        toast.warning('Use soft recovery instead of hard recovery')
        return navigate('/myprofile')
      }
      setUser(res.data)
    } catch (err) {
      console.log(err)
      if (err.response.status === 401) {
        localStorage.removeItem('swToken')
        return navigate('/Login')
      }
    }
    setLoading(false)
  }

  return (
    <div className='flex items-center flex-col justify-center h-screen gap-2'>
      <h1 className='font-bold text-3xl py-4'>Hard Recovery</h1>
      <div className='md:w-2/5 w-1/2 flex flex-col gap-2'>
        <p>
          This is the hard recovery process as you lost your device and you
          don't have any recovery key setup.
        </p>
        <p>
          This will generate a new key for you! Use a device through which
          you'll be using scopex.
        </p>
        <p>
          For future please link more devices, so that you don't loose your
          credentials
        </p>
      </div>
      <button
        disabled={loading}
        className='bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-zinc-900'
        onClick={async () => {
          setLoading(true)
          let payload = await createWebauthnCredential({
            userId: user.dfns_user_id,
            userName: user.username,
            userDisplayName: user.full_name,
          })
          if (payload) navigate('/myprofile')
          setLoading(false)
        }}
      >
        Recover
      </button>
    </div>
  )
}

export default HardRecovery
